/**
 * @file HorizontalTitleCard.tsx
 * @description Horizontal landscape card for generic collections (16:9 aspect ratio)
 *
 * Design specifications:
 * - 16:9 landscape aspect ratio (~500px width)
 * - No progress bar required (unlike ContinueWatchingCard)
 * - Uses horizontalTileUrl, backdropUrlHd, or posterUrlHd
 * - Metadata displayed below the card
 * - Hover reveals Play button overlay
 * - Info button in metadata section
 * - Watchlist toggle button with filled/unfilled state
 *
 * @architecture
 * Generic horizontal card for collections with HORIZONTAL cardType
 */
/* eslint-disable max-lines-per-function */

import type { ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import type { Variants } from "framer-motion";
import { Bookmark, Info, Play } from "lucide-react";
import { useEffect, type ReactElement } from "react";
import { useCascadeFadeIn, useWatchlist } from "../../hooks";
import { pageTransitions } from "../../transitions/pageTransitions";
import { NewBadge } from "../NewBadge";

interface HorizontalTitleCardProps {
  readonly title: ApiTitle;
  readonly index: number;
  readonly className?: string;
  readonly transitionTo: (path: string, options?: Variants) => void;
  readonly onAction?: () => void;
  readonly onInfo?: () => void;
  readonly onWatchlist?: () => void;
  readonly registerWatchlistHandler?: (index: number, handler: () => void) => void;
  readonly hideActions?: boolean;
  readonly hideDetails?: boolean;
}

export function HorizontalTitleCard({
  title,
  index = 0,
  className = "",
  onAction,
  onInfo,
  onWatchlist,
  registerWatchlistHandler,
  transitionTo,
  hideActions = false,
  hideDetails = false,
}: HorizontalTitleCardProps): ReactElement {
  const { inWatchlist, toggle, mutating } = useWatchlist(title.id);
  const { ref, isVisible } = useCascadeFadeIn({ index });

  const handlePlay = () => {
    // Play from beginning (global pointerdown listener exits nav mode)
    transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade);
  };

  const handleMoreInfo = () => {
    transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
  };

  const handleWatchlistToggle = () => {
    void toggle();
  };

  // Register watchlist handler with rail - re-register when toggle changes
  useEffect(() => {
    if (registerWatchlistHandler) {
      const handler = () => {
        void toggle();
      };
      registerWatchlistHandler(index, handler);
    }
  }, [index, registerWatchlistHandler, toggle]);

  // Use callback props if provided, otherwise use internal handlers
  const executeAction = onAction ?? handlePlay;
  const executeInfo = onInfo ?? handleMoreInfo;
  const executeWatchlist = onWatchlist ?? handleWatchlistToggle;

  // Handle click on the entire card when actions are hidden (browse-only mode)
  const handleCardClick = () => {
    if (hideActions) {
      handleMoreInfo(); // Navigate to title detail page
    }
  };

  // Handle keyboard activation for browse-only mode
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (hideActions && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleMoreInfo();
    }
  };

  return (
    <div //NOSONAR
      ref={ref}
      aria-label={`${title.name} - Play`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={hideActions ? "button" : undefined}
      tabIndex={hideActions ? 0 : undefined} //NOSONAR
      className={` 
                group flex-shrink-0 self-start text-left w-[calc(100vw-3rem)] sm:w-[500px] space-y-3 snap-center
                transition-all duration-500 ease-out
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
                ${hideActions ? "cursor-pointer" : ""} 
                ${className}
            `}
    >
      {/* Landscape image with 16:9 aspect ratio */}
      <div className="card-base card-hover-horizontal aspect-video">
        {/* NEW badge for recently added titles */}
        {title.createdAt ? <NewBadge createdAt={title.createdAt} /> : null}

        {/* Backdrop/tile image - prioritize horizontal tile for landscape display */}
        <img
          src={title.horizontalTileUrl ?? title.backdropUrlHd ?? title.posterUrlHd ?? title.posterUrl ?? undefined}
          alt={title.name}
          className="h-full w-full object-cover"
        />

        {/* Gradient overlay on hover - no rotation/scaling on touch devices to prevent overflow */}
        <div className="card-gradient-light" />
        <div className="card-gradient-dark" />

        {/* Play button overlay - shown on hover (hidden when hideActions=true) */}
        {!hideActions && (
          <div
            className="
                        absolute inset-0 flex items-center justify-center
                        opacity-0 group-hover:opacity-100 group-[.rail-focused]:opacity-100
                        transition-opacity delay-100 duration-200 ease-out z-10 pointer-events-none"
          >
            <Button variant="ghost-sm" className="pointer-events-auto" onClick={executeAction}>
              <Play size={20} />
              <span className="ml-2 text-base font-semibold">Play</span>
            </Button>
          </div>
        )}
      </div>

      {/* Metadata below the card */}
      {!hideDetails && (
        <div className="px-1 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <h2 className="font-medium text-white text-base leading-tight line-clamp-2">{title.name}</h2>

              <div className="space-y-1">
                {/* Year, Runtime, and Genres */}
                <div className="flex items-center gap-2 text-xs text-neutral-400 min-w-0">
                  {title.year && <span className="flex-shrink-0">{title.year}</span>}
                  {title.year && title.runtimeMinutes && <span className="flex-shrink-0">•</span>}
                  {title.runtimeMinutes && <span className="flex-shrink-0">{title.runtimeMinutes} min</span>}
                  {(title.year || title.runtimeMinutes) && title.genres && title.genres.length > 0 && (
                    <span className="flex-shrink-0">•</span>
                  )}
                  {title.genres && title.genres.length > 0 && (
                    <span className="truncate">{title.genres.slice(0, 2).join(", ")}</span>
                  )}
                </div>

                {/* Director */}
                {title.directorName && (
                  <p className="text-xs text-neutral-500 truncate">
                    Dir. <span className="text-neutral-400 font-medium">{title.directorName}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Action buttons (hidden when hideActions=true) */}
            {!hideActions && (
              <div className="flex items-center gap-2 pointer-events-none">
                <Button
                  variant="ghost-sm"
                  className="pl-0 flex-shrink-0 pointer-events-auto"
                  onClick={executeWatchlist}
                  disabled={mutating}
                  aria-label={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
                >
                  <Bookmark
                    size={16}
                    fill={inWatchlist ? "currentColor" : "none"}
                    className={inWatchlist ? "text-lumi-accent" : ""}
                  />
                </Button>
                <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={executeInfo}>
                  <Info size={16} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
